import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { parseCustomerAddress } from "./customerAddress.js";
import { evBrand, getEvLogoBytes, logoDataUrl } from "./evBranding.js";
import { evDealerDetails, roundEvAmount } from "./evLoanFormatting.js";
import { brandedSheet, saveBrandedWorkbook } from "./evWorkbook.js";

const display = (value) => value === null || value === undefined || value === "" ? "-" : value;
const amount = (value) => String(roundEvAmount(value));
const isMoneyColumn = (column) => /(INR|amount)/i.test(column.label);
const customerAddress = (value, permanent = false) => {
  const address = parseCustomerAddress(value);
  const parts = permanent ? [address.addressLine1 || address.addressLine, address.city1, address.state1, address.country1, address.pincode1] :
    [address.addressLine, address.city, address.state, address.country, address.pincode];
  return parts.filter(Boolean).join(", ") || "-";
};
const guarantorDetails = (value) => {
  if (typeof value !== "string") return value || {};
  try { return JSON.parse(value) || {}; } catch { return {}; }
};

async function pdfHeader(doc, details, title) {
  const brand = evBrand(details);
  doc.addImage(logoDataUrl(await getEvLogoBytes()), "PNG", 14, 10, 20, 20);
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  const nameLines = doc.splitTextToSize(brand.name, doc.internal.pageSize.getWidth() - 53);
  doc.text(nameLines, 38, 16);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  const addressLines = doc.splitTextToSize(brand.address, doc.internal.pageSize.getWidth() - 53);
  doc.text(addressLines, 38, 16 + nameLines.length * 5);
  const lineY = Math.max(34, 18 + nameLines.length * 5 + addressLines.length * 4);
  doc.setDrawColor(80, 140, 140); doc.line(14, lineY, doc.internal.pageSize.getWidth() - 14, lineY);
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.text(title, 14, lineY + 8);
  return lineY + 13;
}

export async function downloadEvListExcel(title, columns, rows, filename, details) {
  const workbook = XLSX.utils.book_new();
  const sheet = brandedSheet(title, [
    columns.map((column) => column.label),
    ...rows.map((row) => columns.map((column) => isMoneyColumn(column)
      ? roundEvAmount(row[column.key]) : display(row[column.key]))),
  ], details);
  XLSX.utils.book_append_sheet(workbook, sheet, title.slice(0, 31));
  await saveBrandedWorkbook(workbook, filename);
}

export async function downloadEvListPdf(title, columns, rows, filename, details) {
  const doc = new jsPDF({ orientation: columns.length > 6 ? "landscape" : "portrait" });
  const startY = await pdfHeader(doc, details, title);
  autoTable(doc, {
    startY,
    head: [columns.map((column) => column.label)],
    body: rows.map((row) => columns.map((column) => String(isMoneyColumn(column)
      ? roundEvAmount(row[column.key]) : display(row[column.key])))),
    theme: "striped",
    styles: { fontSize: 8 },
  });
  doc.save(`${filename}.pdf`);
}

export async function downloadEvStatementExcel(loan, details) {
  const workbook = XLSX.utils.book_new();
  const guarantor = guarantorDetails(loan.customer?.guarantor);
  const dealer = evDealerDetails(loan.dealer_details, loan.asset_details);
  const summary = [
    ["EV Loan Statement", `Loan #${loan.id}`],
    ["Customer", loan.customer?.full_name || "-"],
    ["Loan account number", loan.loan_account_number || "-"],
    ["Phone", loan.customer?.phoneNumber1 || "-"],
    ["Alternate phone", loan.customer?.phoneNumber2 || "-"],
    ["Date of birth", loan.customer?.dob ? String(loan.customer.dob).slice(0, 10) : "-"],
    ["Father's name", loan.customer?.fatherName || "-"],
    ["Current address", customerAddress(loan.customer?.address)],
    ["Permanent address", customerAddress(loan.customer?.current_address, true)],
    ["Guarantor 1", guarantor.first?.name || "-"],
    ["Guarantor 1 phone", guarantor.first?.mobile || "-"],
    ["Guarantor 2", guarantor.second?.name || "-"],
    ["Guarantor 2 phone", guarantor.second?.mobile || "-"],
    ["Product", loan.product?.name || "-"],
    ["Asset", loan.asset_description],
    ["Dealer", dealer.name || "-"],
    ["Dealer phone", dealer.phone || "-"],
    ["Dealer address", dealer.address || "-"],
    ["Dealer bank", dealer.bank_name || "-"],
    ["Dealer bank branch", dealer.bank_branch || "-"],
    ["Dealer account holder", dealer.account_name || "-"],
    ["Dealer account number", dealer.account_number || "-"],
    ["Dealer IFSC", dealer.ifsc_code || "-"],
    ...Object.entries(typeof loan.asset_details === "string" ? JSON.parse(loan.asset_details) : loan.asset_details || {})
      .filter(([, value]) => value).map(([key, value]) => [key.replaceAll("_", " "), value]),
    ["Loan status", loan.status],
    ["Principal", roundEvAmount(loan.principal)],
    ["Principal outstanding", roundEvAmount(loan.outstanding_principal)],
    ["Annual rate (%)", Number(loan.annual_interest_rate)],
    ["EMI method", loan.emi_type],
    ["Total interest", roundEvAmount(loan.total_interest)],
    ["Paid", roundEvAmount(loan.paid_amount)],
    ["Outstanding", roundEvAmount(loan.outstanding_amount)],
    ["Disbursement date", loan.disbursement_date || "-"],
    ["EMI start date", loan.first_due_date || "-"],
    ["Disbursement mode", loan.disbursement_mode || "-"],
    ["Disbursement reference", loan.disbursement_reference || "-"],
    ["Processing fee", roundEvAmount(loan.processing_charges)],
    ["File charges", roundEvAmount(loan.file_charges)],
    ["Other charges", roundEvAmount(loan.other_charges)],
    ["Net disbursement", roundEvAmount(loan.principal)],
    ["Charges payment mode", loan.charges_payment_mode || "-"],
    ["Charges payment date", loan.charges_payment_date || "-"],
    ["Charges payment reference", loan.charges_payment_reference || "-"],
    ["EMI start date", loan.first_due_date || "-"],
  ];
  XLSX.utils.book_append_sheet(workbook, brandedSheet("EV loan statement", summary, details), "Loan details");
  XLSX.utils.book_append_sheet(workbook, brandedSheet("EMI details", [
    ["EMI No", "Due date", "Principal", "Principal paid", "Principal balance", "Interest", "EMI", "Paid", "Remaining", "Status"],
    ...(loan.schedule || []).map((row) => [row.installment, row.due_date, roundEvAmount(row.principal), roundEvAmount(row.principal_paid),
      roundEvAmount(row.principal_balance), roundEvAmount(row.interest),
      roundEvAmount(row.emi), roundEvAmount(row.paid), roundEvAmount(row.outstanding), row.payment_status]),
  ], details), "EMI details");
  XLSX.utils.book_append_sheet(workbook, brandedSheet("Payments", [
    ["Payment date", "Amount", "Mode", "Reference"],
    ...(loan.payments || []).map((payment) => [payment.payment_date, roundEvAmount(payment.amount), payment.payment_mode, payment.reference]),
  ], details), "Payments");
  await saveBrandedWorkbook(workbook, `EV-Loan-${loan.id}-Statement`);
}

export async function downloadEvStatementPdf(loan, details) {
  const doc = new jsPDF();
  const guarantor = guarantorDetails(loan.customer?.guarantor);
  const dealer = evDealerDetails(loan.dealer_details, loan.asset_details);
  const startY = await pdfHeader(doc, details, "EV loan statement");
  doc.setFontSize(9);
  doc.text(`${loan.loan_account_number || `Loan #${loan.id}`}  |  Status: ${loan.status}`, 14, startY + 2);
  autoTable(doc, {
    startY: startY + 6,
    head: [["Customer details", "Value"]],
    body: [
      ["Customer", display(loan.customer?.full_name)],
      ["Loan account number", display(loan.loan_account_number)],
      ["Phone", display(loan.customer?.phoneNumber1)],
      ["Alternate phone", display(loan.customer?.phoneNumber2)],
      ["Email", display(loan.customer?.email)],
      ["Date of birth", loan.customer?.dob ? String(loan.customer.dob).slice(0, 10) : "-"],
      ["Father's name", display(loan.customer?.fatherName)],
      ["Current address", customerAddress(loan.customer?.address)],
      ["Permanent address", customerAddress(loan.customer?.current_address, true)],
      ["Guarantor 1", guarantor.first?.name || "-"],
      ["Guarantor 1 phone", guarantor.first?.mobile || "-"],
      ["Guarantor 2", guarantor.second?.name || "-"],
      ["Guarantor 2 phone", guarantor.second?.mobile || "-"],
    ],
    theme: "grid",
  });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [["Loan details", "Value"]],
    body: [
      ["Product", display(loan.product?.name)], ["Asset", display(loan.asset_description)],
      ["Dealer", display(dealer.name)], ["Dealer phone", display(dealer.phone)],
      ["Dealer address", display(dealer.address)], ["Dealer bank", display(dealer.bank_name)],
      ["Dealer bank branch", display(dealer.bank_branch)],
      ["Dealer account holder", display(dealer.account_name)],
      ["Dealer account number", display(dealer.account_number)],
      ["Dealer IFSC", display(dealer.ifsc_code)],
      ...Object.entries(typeof loan.asset_details === "string" ? JSON.parse(loan.asset_details) : loan.asset_details || {})
        .filter(([, value]) => value).map(([key, value]) => [key.replaceAll("_", " "), value]),
      ["Purchase price (INR)", amount(loan.price)], ["Down payment (INR)", amount(loan.down_payment)],
      ["Principal (INR)", amount(loan.principal)], ["Annual rate", `${loan.annual_interest_rate}% ${loan.emi_type}`],
      ["Term", `${loan.term_months} months`], ["Monthly EMI (INR)", amount(loan.monthly_emi)],
      ["Total interest (INR)", amount(loan.total_interest)], ["Paid (INR)", amount(loan.paid_amount)],
      ["Outstanding (INR)", amount(loan.outstanding_amount)],
      ["Principal outstanding (INR)", amount(loan.outstanding_principal)],
      ["EMI start date", display(loan.first_due_date)],
      ["Disbursement date", display(loan.disbursement_date)],
      ["Disbursement mode", display(loan.disbursement_mode)],
      ["Disbursement reference", display(loan.disbursement_reference)],
      ["Processing fee collected separately (INR)", amount(loan.processing_charges)], ["File charges collected separately (INR)", amount(loan.file_charges)],
      ["Other charges collected separately (INR)", amount(loan.other_charges)],
      ["Charges payment mode", display(loan.charges_payment_mode)],
      ["Charges payment date", display(loan.charges_payment_date)],
      ["Charges payment reference", display(loan.charges_payment_reference)],
      ["Net disbursement (INR)", amount(loan.principal)],
    ],
    theme: "grid",
  });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [["EMI No", "Due date", "Principal", "Interest", "EMI", "Paid", "Remaining", "Status"]],
    body: (loan.schedule || []).map((row) => [row.installment, row.due_date, amount(row.principal), amount(row.interest),
      amount(row.emi), amount(row.paid), amount(row.outstanding), row.payment_status]),
    theme: "striped",
    styles: { fontSize: 7 },
  });
  if (loan.payments?.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Payment date", "Amount (INR)", "Mode", "Reference"]],
      body: loan.payments.map((payment) => [payment.payment_date, amount(payment.amount), payment.payment_mode, payment.reference]),
      theme: "striped",
    });
  }
  doc.save(`EV-Loan-${loan.id}-Statement.pdf`);
}

export async function downloadEvSanctionPdf(loan, details) {
  if (!["approved", "disbursed", "closed"].includes(loan.status))
    throw new Error("The sanction letter is available after loan approval");
  const doc = new jsPDF();
  const startY = await pdfHeader(doc, details, "EV loan sanction letter");
  const brand = evBrand(details);
  const dealer = evDealerDetails(loan.dealer_details, loan.asset_details);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text(`Date: ${new Date().toISOString().slice(0, 10)}`, 14, startY + 3);
  doc.text(`Reference: ${loan.loan_account_number}`, 14, startY + 9);
  doc.text(`To: ${loan.customer?.full_name || "Borrower"}`, 14, startY + 18);
  doc.text(doc.splitTextToSize(customerAddress(loan.customer?.address), 180), 14, startY + 24);
  doc.setFont("helvetica", "bold"); doc.text("Subject: Sanction of EV asset loan", 14, startY + 39);
  doc.setFont("helvetica", "normal");
  doc.text(doc.splitTextToSize(`Dear ${loan.customer?.full_name || "Customer"}, your application for the following EV asset loan has been approved by ${brand.name}. Fees are collected separately and do not reduce the disbursed amount or change the interest calculation.`, 180), 14, startY + 47);
  autoTable(doc, {
    startY: startY + 61, theme: "grid", styles: { fontSize: 9 },
    head: [["Sanctioned term", "Details"]],
    body: [
      ["Loan account", loan.loan_account_number],
      ["Product", `${loan.product?.name || "EV loan"} (${loan.product?.category || "EV asset"})`],
      ["Asset", loan.asset_description],
      ["Purchase price (INR)", amount(loan.price)],
      ["Down payment (INR)", amount(loan.down_payment)],
      ["Sanctioned principal (INR)", amount(loan.principal)],
      ["Interest", `${loan.annual_interest_rate}% per annum (${loan.emi_type} EMI)`],
      ["Tenure", `${loan.term_months} months`],
      ["Monthly EMI (INR)", amount(loan.monthly_emi)],
      ["First due date", loan.first_due_date],
      ["Total interest (INR)", amount(loan.total_interest)],
      ["Processing fee collected separately (INR)", amount(loan.processing_charges)],
      ["File charges collected separately (INR)", amount(loan.file_charges)],
      ["Other charges collected separately (INR)", amount(loan.other_charges)],
      ["Charges payment mode", display(loan.charges_payment_mode)],
      ["Charges payment date", display(loan.charges_payment_date)],
      ["Net amount to be disbursed (INR)", amount(loan.principal)],
      ["Dealer", display(dealer.name)],
      ["Dealer phone", display(dealer.phone)],
      ["Dealer address", display(dealer.address)],
      ["Dealer bank", display(dealer.bank_name)],
      ["Bank branch", display(dealer.bank_branch)],
      ["Account holder", display(dealer.account_name)],
      ["Account number", display(dealer.account_number)],
      ["IFSC", display(dealer.ifsc_code)],
    ],
  });
  const afterTable = doc.lastAutoTable.finalY + 10;
  doc.text(doc.splitTextToSize("Disbursement is subject to completion of the required documentation and verification. Repayment follows the approved EMI schedule. Please retain this letter with your loan records.", 180), 14, afterTable);
  doc.text(`For ${brand.name}`, 14, Math.min(afterTable + 31, 276));
  doc.text("Authorized signatory", 14, Math.min(afterTable + 39, 284));
  doc.save(`${loan.loan_account_number}-Sanction-Letter.pdf`);
}
